import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  Heading,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
  VStack,
  HStack,
  Link,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { useState } from 'react';

// Import images using Vite's import syntax
const logoBlack = new URL('./assets/l3rn-logo-blk-sm.png', import.meta.url).href;
const logoGray = new URL('./assets/l3rn-logo-gry-xxs.png', import.meta.url).href;
const octopusImg = new URL('./assets/octopus.png', import.meta.url).href;
const shieldIcon = new URL('./assets/icon-sheild-48.png', import.meta.url).href;
const arrowsIcon = new URL('./assets/icon-arrows-48.png', import.meta.url).href;
const boltIcon = new URL('./assets/icon-bolt-48.png', import.meta.url).href;
const heroImage = new URL('./assets/l3rn-hero.png', import.meta.url).href;

// L3RN Color Scheme
const l3rnBlack = '#1E251A';
const l3rnPink = '#FFE5FC';
const l3rnYellow = '#FFF98C';
const l3rnNeutral = '#FFFEF9';

// Styled Button Component
const StyledButton = ({ colorScheme = 'pink', children, ...props }) => {
  const baseStyles = {
    size: 'lg',
    fontSize: 'sm',
    textTransform: 'uppercase' as const,
    letterSpacing: 'wide',
    border: `1px solid ${l3rnBlack}`,
    borderBottom: `3px solid ${l3rnBlack}`,
    borderRight: `3px solid ${l3rnBlack}`,
    _hover: { 
      transform: 'translateY(1px)',
      borderBottom: '2px solid',
      borderRight: '2px solid',
      borderColor: l3rnBlack
    },
    _active: {
      transform: 'translateY(2px)',
      borderBottom: '1px solid transparent',
      borderRight: '1px solid transparent'
    },
    transition: 'all 0.2s',
  };

  const colorSchemes = {
    pink: {
      bg: l3rnPink,
      color: l3rnBlack,
      _hover: { bg: l3rnYellow }
    },
    white: {
      bg: 'white',
      color: l3rnBlack,
      _hover: { bg: l3rnPink }
    },
    neutral: {
      bg: l3rnNeutral,
      color: l3rnBlack,
      _hover: { bg: l3rnYellow }
    },
    black: {
      bg: l3rnBlack,
      color: 'white',
      _hover: { 
        bg: l3rnBlack,
        opacity: 0.9,
        color: 'white'
      }
    }
  };

  const scheme = colorSchemes[colorScheme as keyof typeof colorSchemes] || colorSchemes.pink;

  return (
    <Button
      {...baseStyles}
      {...scheme}
      {...props}
    >
      {children}
    </Button>
  );
};

// Theme variables
const darkBg = l3rnBlack;
const cardBg = l3rnNeutral;
const textSecondary = l3rnBlack;

const App: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  
  // Section styling
  const sectionProps = {
    pt: 0,  // No top padding
    pb: [6, 8, 10],  // Only bottom padding
    px: [4, 6, 8, 12],
    w: '100%',
    maxW: '100%',
    mx: 0
    // No bottom margin
  };
  
  // Container props for consistent content width
  const containerProps = {
    maxW: ['100%', '100%', 'container.xl'],
    mx: 'auto',
    px: [4, 6, 8, 12],
    w: '100%'
  };
  
  // Inner content spacing
  const contentSpacing = {
    spacing: 8,
    heading: {
      mb: 6
    },
    text: {
      mb: 6
    },
    section: {
      mb: 12
    }
  };
  
  // Theme colors
  const bgColor = useColorModeValue('white', darkBg);
  const cardBgColor = useColorModeValue('white', cardBg);

  const features = [
    { 
      icon: shieldIcon, 
      alt: 'Shield icon',
      title: 'Immutable Proof', 
      description: 'Blockchain ensures your achievements are permanent, tamper-proof, and universally recognized.' 
    },
    { 
      icon: arrowsIcon, 
      alt: 'Arrows icon',
      title: 'Seamless Integration', 
      description: 'Designed to connect with any partner learning platform for instant credential issuance.' 
    },
    { 
      icon: boltIcon, 
      alt: 'Bolt icon',
      title: 'Instant Verification', 
      description: 'Lightning-fast verification that allows employers and institutions to check credentials instantly.' 
    },
  ];
  
  // Handler to open the modal
  const handleOpenModal = () => {
    onOpen();
  };

  return (
    <Box minH="100vh" bg={bgColor} w="100%" overflowX="hidden" m={0} fontFamily="'Inter', sans-serif">
      <Box as="section" {...sectionProps} bg={bgColor}>
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            align="center"
            justify="space-between"
            gap={12}
            py={[8, 12]}
            mb={12}
          >
            <Box maxW={{ base: '100%', lg: '48%' }}>
              <Box mb={6}>
                <Image 
                  src={logoBlack} 
                  alt="L3RN" 
                  h="24px" 
                  w="auto"
                />
              </Box>
              <Text 
                color={l3rnBlack}
                fontSize="sm"
                fontWeight="bold"
                letterSpacing="wide"
                textTransform="uppercase"
                mb={4}
              >
                The Next Generation of Digital Credentials
              </Text>
              <Heading 
                as="h1" 
                size={['2xl', '3xl', '4xl']} 
                fontWeight="extrabold" 
                mb={6}
                lineHeight="1.2"
              >
                Securely <Box as="span" color={l3rnBlack}>Issue</Box> & <Box as="span" color={l3rnBlack}>Verify</Box> Certifications
              </Heading>
              <Text 
                fontSize={['md', 'lg']} 
                color={textSecondary} 
                mb={8}
                maxW="2xl"
                mx={{ base: 'auto', lg: '0' }}
              >
                L3RN provides the dedicated, decentralized service that issues blockchain certifications on behalf of connected partner entities. We turn your users achievements into their verifiable, owned identity.
              </Text>
              <HStack spacing={4} justify={{ base: 'center', lg: 'flex-start' }}>
                <StyledButton 
                  colorScheme="white"
                  rightIcon={<ChevronRightIcon />}
                  onClick={handleOpenModal}
                >
                  Join the Beta
                </StyledButton>
              </HStack>
            </Box>
            
            <Box 
              maxW={['100%', '100%', '48%']} 
              position="relative"
              py={16}
            >
              <Image
                src={heroImage}
                alt="Digital credentials for the next phase of the web"
                width="100%"
                height="auto"
                borderRadius="2xl"
              />
            </Box>
          </Flex>
      </Box>

        {/* Features Section */}
        <Box as="section" {...sectionProps} bg={bgColor} id="features" mb={8}>
          
            <Box maxW="3xl" mx="auto" textAlign="center" mb={contentSpacing.section.mb}>
            <Text 
              color={l3rnBlack}
              fontSize="sm"
              fontWeight="bold"
              letterSpacing="wide"
              textTransform="uppercase"
              mb={4}
            >
              Why L3RN
            </Text>
            <Heading as="h2" size="2xl" fontWeight="extrabold" mb={6}>
              The Dedicated Issuance Service
            </Heading>
            <Text fontSize="lg" color={textSecondary} maxW="2xl" mx="auto">
              L3RN is built purely to handle the secure, fast, and auditable minting function for educational and professional organizations.
            </Text>
          </Box>
          
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mt={12}>
            {features.map((feature, index) => (
              <Box
                key={index}
                p={8}
                borderRadius="xl"
                bg={l3rnPink}
                _hover={{
                  transform: 'translateY(-5px)',
                  transition: 'all 0.3s ease',
                  boxShadow: 'xl'
                }}
              >
                <Box mb={4}>
                  <Image 
                    src={feature.icon} 
                    alt={feature.alt}
                    h="48px"
                    w="auto"
                  />
                </Box>
                <Heading as="h3" size="lg" mb={4} fontWeight="semibold">
                  {feature.title}
                </Heading>
                <Text color={textSecondary} lineHeight="tall">
                  {feature.description}
                </Text>
              </Box>
            ))}
            </SimpleGrid>
        </Box>

        {/* Beta Partners Section */}
        <Box as="section" {...sectionProps} color={l3rnNeutral}>
          <Box bg={l3rnBlack} rounded="2xl" mb={8}>
          <Flex 
            direction={{ base: 'column', lg: 'row' }}
            align="center"
            justify="space-between"
            gap={8}
            maxW="container.xl"
            mx="auto"
            px={[4, 6, 8, 12]}
            py={12}
          >
            {/* Text Content - Left Side */}
            <Box 
              maxW={{ base: '100%', lg: '50%' }}
              textAlign={{ base: 'center', lg: 'left' }}
              pr={{ lg: 8 }}
            >
              <Text 
                fontSize="sm"
                fontWeight="bold"
                letterSpacing="wide"
                textTransform="uppercase"
                color={l3rnNeutral}
                mb={4}
              >
                Beta Partners
              </Text>
              <Heading 
                as="h2" 
                size="2xl" 
                fontWeight="extrabold" 
                color={l3rnNeutral} 
                mb={4}
              >
                Issuing Credentials Today
              </Heading>
              <Text color={l3rnNeutral} mb={6}>
                Leading organisations are already trialling our platform for secure, automated credential issuance.
              </Text>
              
            </Box>
            
            {/* Card - Right Side */}
            <Box 
              w={{ base: '100%', lg: '45%' }}
              maxW="500px"
              borderRadius="xl"
              overflow="hidden"
              bg={l3rnNeutral}
              _hover={{
                transform: 'translateY(-4px)',
                boxShadow: 'xl',
                transition: 'all 0.2s',
                '& .cta-button': {
                  bg: 'white',
                  color: 'gray.900',
                }
              }}
              transition="all 0.2s"
            >
                  <Box 
                    w="100%" 
                    h="180px" 
                    position="relative"
                    overflow="hidden"
                  >
                    <Image
                      src={octopusImg}
                      alt="Creatures of Design"
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      objectPosition="center"
                    />
                    <Box
                      position="absolute"
                      bottom="0"
                      left="0"
                      right="0"
                      bg="rgba(30, 37, 26, 0.8)"
                      p={6}
                    >
                      <Text 
                        fontSize="md" 
                        fontWeight="bold" 
                        textTransform="uppercase"
                        color={l3rnNeutral} 
                        mb={0}
                        textAlign="left"
                      >
                        Creatures of Design
                      </Text>
                    </Box>
                  </Box>
                  <Box p={6}>
                    <Text color={textSecondary} mb={6} textAlign="left">
                      Empowering a new generation of designers and utilizing L3RN for verifiable training certificates.
                    </Text>
                    <StyledButton
                      as="a"
                      href="https://creaturesofdesign.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      w="100%"
                      colorScheme="white"
                    >
                      Visit Partner Site
                    </StyledButton>
                  </Box>
                </Box>
          </Flex>
          </Box>
        </Box>

        {/* CTA Section */}
        <Box as="section" {...sectionProps} bg={bgColor}>
          <Box bg={l3rnYellow} rounded="2xl" textAlign="center">
            <VStack spacing={contentSpacing.spacing} maxW="3xl" mx="auto" py={[8, 12]}>
              <Text 
                color={l3rnBlack}
                fontSize="sm"
                fontWeight="bold"
                letterSpacing="wide"
                textTransform="uppercase"
                mb={4}
              >
                Early Access
              </Text>
              <Heading as="h2" size="2xl" color={l3rnBlack} fontWeight="extrabold" mb={6}>
                Ready to Connect Your Platform?
              </Heading>
              <Text fontSize="lg" color={l3rnBlack} mb={8} maxW="2xl" mx="auto">
                We are currently onboarding select partners for our private beta. Apply now to leverage instant, verifiable credential issuance.
              </Text>
              <StyledButton
                colorScheme="white"
                rightIcon={<ChevronRightIcon />}
                onClick={() => handleOpenModal('test')}
                px={8}
                py={6}
              >
                Get in touch
              </StyledButton>
            </VStack>
          </Box>
        </Box>

        {/* Footer */}
        <Box as="footer" py={12} px={12}>
          <Image
            src={logoBlack}
            alt="L3RN"
            h="16px"
            w="auto"
          />
            <Text mt={4} textAlign="left" color={textSecondary} fontSize="xs">
              © {new Date().getFullYear()} Temporal Dynamics. All rights reserved.
            </Text>
        </Box>
        
        {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
        <ModalContent 
          bg={cardBg}
          border="1px"
          borderColor="whiteAlpha.200"
          borderRadius="2xl"
          overflow="hidden"
        >
          <ModalCloseButton top={4} right={4} color={textSecondary} />
          <ModalBody p={0}>
            <VStack
              as="form"
              onSubmit={(e) => {
                e.preventDefault();
                // Form submission logic will be added later
                onClose();
              }}
              spacing={8}
              p={[6, 8]}
              textAlign="center"
            >
              <Box>
                <Text 
                  fontSize="sm"
                  fontWeight="bold"
                  letterSpacing="wide"
                  textTransform="uppercase"
                  mb={2}
                  color={l3rnBlack}
                >
                  Early Access
                </Text>
                <Heading size="lg" fontWeight="bold" color={l3rnBlack} mb={2}>
                  Join Our Beta Program
                </Heading>
                <Text color={textSecondary}>
                  Leave your details and we'll be in touch about the beta program.
                </Text>
              </Box>
              
              <VStack spacing={4} w="full">
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="lg"
                    borderColor="gray.200"
                    _hover={{ borderColor: 'gray.300' }}
                    _focus={{ borderColor: 'pink.500', boxShadow: '0 0 0 1px var(--chakra-colors-pink-500)' }}
                    bg="white"
                  />
                </FormControl>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    size="lg"
                    borderColor="gray.200"
                    _hover={{ borderColor: 'gray.300' }}
                    _focus={{ borderColor: 'pink.500', boxShadow: '0 0 0 1px var(--chakra-colors-pink-500)' }}
                    bg="white"
                  />
                </FormControl>
                <StyledButton 
                  w="full" 
                  type="submit"
                  colorScheme="pink"
                  mt={2}
                >
                  Submit
                </StyledButton>
              </VStack>
              
              <Text fontSize="sm" color={textSecondary}>
                We'll be in touch soon!
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default App;